import { addDays, differenceInCalendarDays } from 'date-fns'

export type TaskSummary = {
  id: string
  title: string
  description: string | null
  dueDate: string
  status: string | null
  itemId: string | null
  itemName: string | null
  itemImageUrl: string | null
}

export type WarrantySummary = {
  id: string
  name: string
  category: string
  warrantyExpiry: string
  daysUntilExpiry: number
  imageUrl: string | null
}

export type MissingPart = {
  partId: string
  partName: string
  needed: number
  available: number
}

export type NextUpResult =
  | { type: 'overdue'; item: TaskSummary; daysOverdue: number }
  | { type: 'due_today'; item: TaskSummary }
  | { type: 'upcoming'; item: TaskSummary; daysUntilDue: number }
  | { type: 'warranty'; item: WarrantySummary; daysUntilExpiry: number }
  | { type: 'blocked_only'; item: TaskSummary; daysOverdue: number; missingParts: MissingPart[] }
  | { type: 'caught_up'; nextItem: TaskSummary | null }

// Calendar-day diff (a - b). Uses date-fns to count wall-clock day boundaries
// rather than dividing milliseconds — adjacent local-midnight Dates straddling
// a DST transition are 23h or 25h apart, which inflates a Math.ceil(ms/86400000)
// result by 1 across the November fall-back.
export function daysBetween(a: Date, b: Date): number {
  return differenceInCalendarDays(a, b)
}

export function startOfDay(d: Date): Date {
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  return copy
}

/**
 * Parse a YYYY-MM-DD string as a Date at LOCAL midnight (not UTC midnight).
 * `new Date('2026-04-24')` parses as UTC midnight, which is the previous local day
 * for any timezone west of UTC. Use this whenever comparing against `new Date()` or
 * `startOfDay(new Date())`.
 */
export function parseLocalDate(yyyyMmDd: string): Date {
  const [y, m, d] = yyyyMmDd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/**
 * Priority logic. Picks exactly one item for the hero.
 * Order: overdue → due today → upcoming (within 7 days) → warranty (within 30 days) → blocked_only → caught_up.
 *
 * Tasks in `blockedTaskIds` are skipped at overdue/due_today/upcoming tiers. If every
 * actionable candidate is blocked but blocked tasks exist, we fall into `blocked_only`
 * with the most urgent blocked task (oldest overdue first, then today, then upcoming).
 *
 * Pure function. No I/O. Safe to call from client or server.
 */
export function getNextUp(input: {
  tasks: TaskSummary[]
  warranties: WarrantySummary[]
  now: Date
  blockedTaskIds?: Set<string>
  blockedTaskDetails?: Map<string, MissingPart[]>
}): NextUpResult {
  const { tasks, warranties, now, blockedTaskIds, blockedTaskDetails } = input
  const today = startOfDay(now)
  const sevenDaysOut = addDays(today, 7)

  const isBlocked = (t: TaskSummary) => blockedTaskIds?.has(t.id) ?? false

  const incomplete = tasks.filter((t) => t.status !== 'completed')

  // Use parseLocalDate (NOT `new Date(t.dueDate)`): YYYY-MM-DD parsed via the Date
  // constructor lands at UTC midnight, which is the previous local day in any tz
  // west of UTC — making today's tasks compare equal to yesterday's `today` and
  // breaking the dueToday/overdue boundaries on non-UTC runtimes (dev machines).
  const overdue = incomplete
    .filter((t) => parseLocalDate(t.dueDate) < today)
    .sort((a, b) => parseLocalDate(a.dueDate).getTime() - parseLocalDate(b.dueDate).getTime())

  const firstActionableOverdue = overdue.find((t) => !isBlocked(t))
  if (firstActionableOverdue) {
    return {
      type: 'overdue',
      item: firstActionableOverdue,
      daysOverdue: daysBetween(today, parseLocalDate(firstActionableOverdue.dueDate)),
    }
  }

  const dueToday = incomplete.filter(
    (t) => parseLocalDate(t.dueDate).getTime() === today.getTime()
  )
  const firstActionableDueToday = dueToday.find((t) => !isBlocked(t))
  if (firstActionableDueToday) {
    return { type: 'due_today', item: firstActionableDueToday }
  }

  const upcoming = incomplete
    .filter((t) => {
      const d = parseLocalDate(t.dueDate)
      return d > today && d <= sevenDaysOut
    })
    .sort((a, b) => parseLocalDate(a.dueDate).getTime() - parseLocalDate(b.dueDate).getTime())
  const firstActionableUpcoming = upcoming.find((t) => !isBlocked(t))
  if (firstActionableUpcoming) {
    return {
      type: 'upcoming',
      item: firstActionableUpcoming,
      daysUntilDue: daysBetween(parseLocalDate(firstActionableUpcoming.dueDate), today),
    }
  }

  const warrantySorted = [...warranties].sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry)
  if (warrantySorted[0]) {
    return {
      type: 'warranty',
      item: warrantySorted[0],
      daysUntilExpiry: warrantySorted[0].daysUntilExpiry,
    }
  }

  // Fallback: every actionable tier was empty or fully blocked. If any blocked task exists
  // within the hero-relevant window, surface it so the user knows why the hero is quiet.
  const firstBlocked =
    overdue.find(isBlocked) ?? dueToday.find(isBlocked) ?? upcoming.find(isBlocked)
  if (firstBlocked) {
    const daysOverdueRaw = daysBetween(today, parseLocalDate(firstBlocked.dueDate))
    return {
      type: 'blocked_only',
      item: firstBlocked,
      daysOverdue: Math.max(0, daysOverdueRaw),
      missingParts: blockedTaskDetails?.get(firstBlocked.id) ?? [],
    }
  }

  const futureTasks = incomplete
    .filter((t) => parseLocalDate(t.dueDate) > sevenDaysOut)
    .sort((a, b) => parseLocalDate(a.dueDate).getTime() - parseLocalDate(b.dueDate).getTime())

  return { type: 'caught_up', nextItem: futureTasks[0] ?? null }
}
